/* eslint-disable @typescript-eslint/no-explicit-any -- legacy stuffing response shapes */
import { ReactNode, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import cube3dIcon from "assets/images/package-type/cube-3d.svg";
import { Barrels, BigBag, Box, Pipes, Rolls, Sack } from "components/UI/Icons";
import { Modal } from "components/UI/Modal";
import useEscape from "hook/useEscape";
import { ImageContext } from "pages/StuffingPage";
import { Group, ResponseContainer, RootState } from "store";
import { convertUnits, getProductsValues, getTransportType, typeToString } from "utils/utils";
import { RenderModal } from "../../RenderModal/RenderModal";
import { StuffingChart } from "./StuffingChart";
import renderModalStyles from "../../RenderModal/RenderModal.module.scss";
import styles from "./StuffingDetailed.module.scss";

interface StuffingDetailedItemProps {
  containerData: ResponseContainer;
  cargoesAttributes: any;
  index: number;
  options: any;
}

const PACKAGE_LABEL: Record<string, string> = {
  box: "Box",
  bigbags: "Big Bags",
  barrels: "Barrels",
  sacks: "Sacks",
  pipes: "Pipes",
  roll: "Rolls",
  bulk: "Box",
};

const PACKAGE_ICON: Record<string, ReactNode> = {
  box: <Box width={16} height={16} />,
  bulk: <Box width={16} height={16} />,
  bigbags: <BigBag width={16} height={16} />,
  barrels: <Barrels width={16} height={16} />,
  sacks: <Sack width={16} height={16} />,
  pipes: <Pipes width={16} height={16} />,
  roll: <Rolls width={16} height={16} />,
};

const findGroupName = (groups: Group[], productUid: string) => {
  for (const group of groups) {
    if (group.items?.some((item) => String(item.uid) === String(productUid))) {
      return group.name || "—";
    }
  }
  return "—";
};

export const StuffingDetailedItem = ({
  containerData,
  index,
  options,
  cargoesAttributes,
}: StuffingDetailedItemProps) => {
  const images = useContext(ImageContext);
  const groups = useSelector((state: RootState) => state.root.groups);
  const [isModal, setModal] = useState(false);
  const { t } = useTranslation();
  const closeModal = () => setModal(false);
  useEscape(() => setModal(false));

  const productData = useMemo(
    () => getProductsValues(containerData.general.items, cargoesAttributes),
    [containerData.general.items, cargoesAttributes]
  );

  const rows = useMemo(() => {
    let previousGroup = "";
    return productData.map((item) => {
      const groupName = findGroupName(groups, item.uid);
      const showGroup = groupName !== previousGroup;
      previousGroup = groupName;
      const type = item.type || "box";
      return {
        ...item,
        groupName,
        showGroup,
        packageLabel: PACKAGE_LABEL[type] ?? type,
        packageIcon: PACKAGE_ICON[type] ?? PACKAGE_ICON.box,
      };
    });
  }, [productData, groups]);

  const volumeUnit = options.lengthUnits === "mm" ? "m3" : `${options.lengthUnits}3`;
  const cargoVolume =
    options.lengthUnits === "mm"
      ? containerData.general.cargoVolume / 1_000_000_000
      : containerData.general.cargoVolume;
  const filledPercent = containerData.attr.volume
    ? Math.min(100, Math.floor((containerData.general.cargoVolume / containerData.attr.volume) * 100))
    : 0;
  const emptyVolume = Math.max(containerData.attr.volume - containerData.general.cargoVolume, 0);

  const transportKind = getTransportType(containerData.attr.type);
  const title = `${typeToString(containerData.attr.type) || (transportKind === "truck" ? t("Custom Truck") : t("Custom Container"))} #${index}`;
  const unitLabel = `${containerData.qty} ${containerData.qty > 1 ? t("units") : t("unit")}`;
  const cargoesLabel = containerData.general.itemQty === 1 ? t("cargo") : t("cargoes");

  return (
    <>
      <article className={styles.card} data-transport={transportKind}>
        <div className={styles.preview}>
          <div className={styles.previewHeader}>
            <h3 className={styles.title}>{title}</h3>
            <span className={styles.unitCount}>{unitLabel}</span>
          </div>

          <div className={styles.previewImage}>
            {images[containerData.uid] ? <img src={images[containerData.uid]} alt="" /> : null}
          </div>

          <button type="button" className={styles.view3d} onClick={() => setModal(true)}>
            <span className={styles.view3dIcon}>
              <img src={cube3dIcon} alt="" width={14} height={14} />
            </span>
            <span>{t("3D view")}</span>
          </button>
        </div>

        <div className={styles.divider} aria-hidden />

        <div className={styles.details}>
          <div className={styles.detailsTop}>
            <StuffingChart productsData={productData} filledPercent={filledPercent} emptyVolume={emptyVolume} />

            <div className={styles.products}>
              <div className={styles.colGroup}>
                <span className={styles.colHeader}>{t("Group")}</span>
                <div className={styles.colBody}>
                  {rows.map((row) => (
                    <div key={`g-${row.uid}`} className={styles.rowCell}>
                      {row.showGroup ? <span className={styles.groupName}>{row.groupName}</span> : null}
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.colProduct}>
                <span className={styles.colHeader}>{t("Product name")}</span>
                <div className={styles.colBody}>
                  {rows.map((row) => (
                    <div key={`p-${row.uid}`} className={styles.productCell}>
                      <span className={styles.dot} style={{ backgroundColor: String(row.color) }} />
                      <span className={styles.productName} title={row.name}>
                        {row.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.colPackage}>
                <span className={styles.colHeader}>{t("Package type")}</span>
                <div className={styles.colBody}>
                  {rows.map((row) => (
                    <div key={`pkg-${row.uid}`} className={styles.packageCell}>
                      <span className={styles.packageIcon}>{row.packageIcon}</span>
                      <span className={styles.packageLabel}>{t(row.packageLabel)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.colQty}>
                <span className={styles.colHeader}>{t("Q-ty")}</span>
                <div className={styles.colBody}>
                  {rows.map((row) => (
                    <div key={`q-${row.uid}`} className={styles.valueCell}>
                      {row.qty}
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.colVolume}>
                <span className={styles.colHeader}>
                  {t("Volume")}, {volumeUnit}
                </span>
                <div className={styles.colBody}>
                  {rows.map((row) => (
                    <div key={`v-${row.uid}`} className={styles.valueCell}>
                      {(options.lengthUnits === "mm" ? row.volume / 1_000_000_000 : row.volume).toFixed(2)}
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.colWeight}>
                <span className={styles.colHeader}>
                  {t("Weight")}, {t(options.weightUnits)}
                </span>
                <div className={styles.colBody}>
                  {rows.map((row) => (
                    <div key={`w-${row.uid}`} className={styles.valueCell}>
                      {convertUnits(row.weight, options.weightUnits).toFixed(2)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.summary}>
            <div className={styles.summaryLabels}>
              <span>{t("Total")}</span>
              <span>{t("Cargo volume")}</span>
              <span>{t("Cargo weight")}</span>
            </div>
            <div className={styles.summaryValues}>
              <span>
                {containerData.general.itemQty} {cargoesLabel}
              </span>
              <span>
                {cargoVolume.toFixed(2)} {volumeUnit}
              </span>
              <span>
                {convertUnits(containerData.general.cargoWeight, options.weightUnits).toFixed(2)}{" "}
                {t(options.weightUnits)}
              </span>
            </div>
          </div>
        </div>
      </article>

      <Modal isOpen={isModal} onClose={closeModal} modalClassname={renderModalStyles.modal}>
        <RenderModal closeModal={closeModal} responseData={containerData} cargoesAttributes={cargoesAttributes} />
      </Modal>
    </>
  );
};
